import EntityType from '@enums/EntityType';
import { useSkillContext } from '@providers/skill/SkillProvider';
import nestedEntityResponse from '@utils/api/nestedEntityResponse';
import useLibrary from '@pkg/entities/library/useLibrary';
import useDesignStore from '@/components/DesignContainer/hooks/useDesignStore';
import useAdditionalDesignData from './useAdditionalDesignData';

/**
 * A temporary hook to handle requests to get data from the snapshot.
 */
const useGetApi = () => {
  const { peopleSkillMap } = useSkillContext();
  const library = useLibrary();
  const loadRevision = useAdditionalDesignData();

  // Retrieves the requested data from the scenario.
  const getData = async (body) => {
    const { additional, design, level, snapshot } = useDesignStore.getState();
    const state = useDesignStore.getState();

    // Insights charts pass an explicit scope level via the request body; every
    // other consumer falls back to the design level from the store.
    const effectiveLevel = body?.level ?? level;

    // Create the list of requested designs.
    const designs = [
      {
        id: 'primary',
        entities: snapshot.entities,
      },
    ];

    // Load the requested designs as necessary.
    if (body.settings.designs?.length) {
      for (const design of body.settings.designs) {
        // Skip the primary design we've already added.
        if (design.type === 'primary') {
          continue;
        }

        // This will load the designs from the store but if they haven't yet
        // been loaded, it will wait for them to be hydrated and loaded before
        // returning the design data.
        const loadedDesign = await loadRevision(
          design.id,
          design.revisionId,
          design.scenarioType ?? EntityType.ORGANISATION,
          false
        );

        if (!loadedDesign) {
          continue;
        }

        designs.push({
          id: `${design.id}:${design.revisionId}`,
          entities: loadedDesign.snapshot.entities,
          name: design.name,
          colors: design.colors,
        });
      }
    } else {
      designs.push({ id: 'primary', entities: snapshot.entities });
    }

    // Takes the request parameters and the loaded data to return a
    // structured response.
    const data = nestedEntityResponse({
      designs,
      designLevel: level,
      entities: snapshot.entities,
      body,
      level: effectiveLevel,
      library,
      peopleSkillMap,
    });

    return data;
  };

  return {
    getData,
  };
};

export default useGetApi;
